import { useCallback, useRef, useState } from "react";
import { Upload, FileText, Trash2, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

function CandidateRow({ c, studyId, editable, onChange, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ display_name: c.display_name, title: c.title || "" });
  const save = async () => {
    try {
      const r = await api.put(`/admin/studies/${studyId}/candidates/${c.id}`, { display_name: form.display_name, title: form.title });
      onChange(r.data);
      setEdit(false);
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div data-testid={`candidate-row-${c.id}`} className="flex items-center gap-4 px-5 py-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-plena-surface2 text-plena-text2"><FileText className="h-4 w-4" /></div>
      {edit ? (
        <div className="flex flex-1 flex-col gap-2 sm:flex-row">
          <Input data-testid={`candidate-name-input-${c.id}`} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Ad Soyad" className="h-9" />
          <Input data-testid={`candidate-title-input-${c.id}`} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Unvan (isteğe bağlı)" className="h-9" />
          <Button size="sm" data-testid={`candidate-save-${c.id}`} onClick={save} className="h-9"><Check className="h-4 w-4" /></Button>
        </div>
      ) : (
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{c.display_name}</p>
          <p className="truncate text-sm text-plena-text2">{c.title || "Unvan belirtilmedi"} · {c.source_file}</p>
        </div>
      )}
      <span className="hidden text-xs font-medium text-plena-success sm:inline">Hazır</span>
      {editable && !edit && (
        <>
          <Button variant="ghost" size="icon" data-testid={`candidate-edit-${c.id}`} onClick={() => setEdit(true)} aria-label="Düzenle"><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" data-testid={`candidate-delete-${c.id}`} onClick={() => onDelete(c)} aria-label="Sil" className="text-plena-text2 hover:text-plena-coral"><Trash2 className="h-4 w-4" /></Button>
        </>
      )}
    </div>
  );
}

export function CandidateUpload({ studyId, candidates, onChange, editable = true, active = false }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(null);

  const upload = useCallback(async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    const fd = new FormData();
    list.forEach((f) => fd.append("files", f));
    setProgress(0);
    try {
      const r = await api.post(`/admin/studies/${studyId}/candidates`, fd, {
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 50),
      });
      onChange([...candidates, ...r.data.created]);
      r.data.errors.forEach((er) => toast.error(`${er.file}: ${er.error}`));
      if (r.data.created.length) toast.success(`${r.data.created.length} CV yüklendi`);
      if (r.data.reopened_assignments) toast.info(`${r.data.reopened_assignments} tamamlanmış değerlendirme yeni adayı puanlamak üzere yeniden açıldı`, { duration: 8000 });
    } catch (e) { toast.error(errText(e)); } finally { setProgress(null); }
  }, [studyId, candidates, onChange]);

  const remove = async (c) => {
    if (active && !window.confirm(`${c.display_name} kaldırılsın mı? Bu adaya tüm değerlendiricilerin verdiği puanlar da silinecek.`)) return;
    try { await api.delete(`/admin/studies/${studyId}/candidates/${c.id}`); onChange(candidates.filter((x) => x.id !== c.id)); toast.success("Aday kaldırıldı"); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-4">
      {editable && (
        <div
          data-testid="cv-dropzone"
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
          className={cn("flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-12 text-center transition-colors duration-200", drag ? "border-plena-blue bg-[#F3F8FF]" : "border-plena-border bg-white hover:border-[#C9CDD6]")}
        >
          <Upload className="mb-3 h-5 w-5 text-plena-text2" />
          <p className="font-medium">CV dosyalarını buraya sürükleyin</p>
          <p className="mt-1 text-sm text-plena-text2">veya seçmek için tıklayın · PDF veya DOCX · en fazla 15 MB</p>
          <input ref={inputRef} data-testid="cv-file-input" type="file" accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
          {progress !== null && (
            <div className="mt-5 w-full max-w-xs">
              <div className="h-1.5 overflow-hidden rounded-full bg-plena-surface2"><div className="h-full rounded-full bg-plena-blue transition-[width]" style={{ width: `${progress}%` }} /></div>
              <p className="mt-2 text-xs text-plena-text2">Yükleniyor… {progress}%</p>
            </div>
          )}
        </div>
      )}
      {candidates.length > 0 ? (
        <div className="plena-card divide-y divide-plena-border" data-testid="candidate-list">
          {candidates.map((c) => (
            <CandidateRow key={c.id} c={c} studyId={studyId} editable={editable} onDelete={remove} onChange={(u) => onChange(candidates.map((x) => (x.id === u.id ? u : x)))} />
          ))}
        </div>
      ) : (
        <p data-testid="candidate-empty" className="text-sm text-plena-text2">Henüz aday yüklenmedi.</p>
      )}
    </div>
  );
}
